import * as THREE from 'three';

const PRIMITIVES = {
  box: { command: 'addBox', label: 'Add Box', y: 0.5 },
  sphere: { command: 'addSphere', label: 'Add Sphere', y: 0.5 },
  cylinder: { command: 'addCylinder', label: 'Add Cylinder', y: 0.5 },
};

export function createAddPalette({ root, viewport, bridge, selection, localExecution }) {
  const body = root.querySelector('#add-palette-body') ?? document.createElement('div');
  body.id = 'add-palette-body';
  body.className = 'add-palette-body';
  if (!body.parentNode) root.append(body);
  const error = document.createElement('div');
  error.className = 'panel-error';
  error.setAttribute('role', 'alert');
  let pointer = null;

  for (const [type, primitive] of Object.entries(PRIMITIVES)) {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.addType = type;
    button.innerHTML = `<span class="primitive-preview ${type}" aria-hidden="true"></span><span>${primitive.label.slice(4)}</span>`;
    body.append(button);
  }
  body.append(error);

  const canvas = viewport.renderer.domElement;
  const rememberPointer = (event) => {
    pointer = { x: event.clientX, y: event.clientY };
  };
  canvas.addEventListener('pointermove', rememberPointer);

  function positionFor(type) {
    const primitive = PRIMITIVES[type];
    const target = viewport.controls.target.clone();
    if (pointer) {
      const bounds = canvas.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((pointer.x - bounds.left) / bounds.width) * 2 - 1,
        -((pointer.y - bounds.top) / bounds.height) * 2 + 1,
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(ndc, viewport.camera);
      raycaster.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), target);
    }
    return [target.x, primitive.y, target.z];
  }

  function emitError(action, caught) {
    error.textContent = caught?.message ?? String(caught);
    root.dispatchEvent(new CustomEvent('manualerror', {
      bubbles: true,
      detail: { action, error: error.textContent },
    }));
  }

  async function add(type) {
    const primitive = PRIMITIVES[String(type).toLowerCase()];
    if (!primitive) throw new Error(`unknown primitive '${type}'`);
    error.textContent = '';
    try {
      const args = { position: positionFor(String(type).toLowerCase()) };
      // Local Execution / Cloud Sync v1 — addBox executes locally and is
      // visible immediately when eligible; the durable commit happens
      // asynchronously. Every other primitive (sphere, cylinder) and any
      // ineligible addBox stays on the existing gated bridge.execute().
      const result = localExecution?.eligible(primitive.command)
        ? localExecution.execute(primitive.command, args)
        : await bridge.execute(primitive.command, args, { label: primitive.label });
      selection.select(result.id);
      close();
      return result;
    } catch (caught) {
      emitError(primitive.command, caught);
      throw caught;
    }
  }

  function openAt(event = {}) {
    if (Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
      pointer = { x: event.clientX, y: event.clientY };
    }
    root.dispatchEvent(new CustomEvent('addpaletteopen', {
      bubbles: true,
      detail: pointer ? { ...pointer } : null,
    }));
  }

  function close() {
    root.dispatchEvent(new CustomEvent('addpaletteclose', { bubbles: true }));
  }

  const click = (event) => {
    const button = event.target.closest?.('[data-add-type]');
    if (button) void add(button.dataset.addType).catch(() => {});
  };
  body.addEventListener('click', click);

  return { openAt, close, add };
}
